import { ButtonIcon, Card, Dialog, Table, useDialog } from "@fpsoftware/mvp";
import { Instant } from "@js-joda/core";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { uniq } from "lodash-es";
import { type ChangeEvent, useMemo, useState } from "react";
import { TbPlus, TbSparkles, TbTag, TbTrash } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import {
  type Address,
  type Borrower,
  formatAddress,
  formatInstantDate,
  formatMoney,
  formatPercent,
  type Mortgage,
  RFQ_STATUS_LABELS,
  RFQ_STATUSES,
  type RfqStatus,
} from "shared";
import { useAddressesSuspense } from "@/addresses/hooks";
import { useBorrowersSuspense } from "@/borrower/hooks";
import { NoContent } from "@/common/NoContent";
import { Button } from "@/components/Button";
import { Input } from "@/components/form/Input";
import { Container } from "@/components/grid/Container";
import { Grid } from "@/components/grid/Grid";
import { Pagination } from "@/components/Pagination";
import { LinkGen } from "@/LinkGen";
import { AppPage } from "@/layout/AppLayout";
import { ChangeRfqStatusDialog } from "@/mortgages/ChangeRfqStatusDialog";
import {
  useMortgageDelete,
  useMortgageList,
  useMortgagesSuspense,
} from "@/mortgages/hooks";
import styles from "./PageListMortgages.module.css";

const columnHelper = createColumnHelper<MortgageRow>();

const columns = [
  columnHelper.accessor((row) => row.mortgage.ID, {
    id: "ID",
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor(
    (row) => `${row.borrower.firstName} ${row.borrower.lastName}`,
    {
      id: "Borrower",
      cell: (info) => info.getValue(),
    },
  ),
  columnHelper.accessor(
    (row) =>
      formatAddress([
        row.address.line1,
        row.address.line2,
        row.address.city,
        row.address.state,
        row.address.zip_code,
      ]),
    {
      id: "Address",
      cell: (info) => info.getValue(),
    },
  ),
  columnHelper.accessor((row) => row.mortgage.data.balance, {
    id: "balance",
    header: "Balance",
    meta: { align: "right" },
    cell: (info) => formatMoney(info.getValue()),
  }),
  columnHelper.accessor((row) => row.mortgage.data.interestRate, {
    id: "interestRate",
    header: "Interest Rate",
    meta: { align: "right" },
    cell: (info) => formatPercent(info.getValue()),
  }),
  columnHelper.accessor((row) => row.mortgage.status, {
    id: "status",
    header: "Status",
    cell: (info) => RFQ_STATUS_LABELS[info.getValue()],
  }),
  columnHelper.accessor((row) => row.mortgage.updatedAt.toEpochMilli(), {
    id: "updatedAt",
    header: "Last Updated",
    cell: (info) => formatInstantDate(Instant.ofEpochMilli(info.getValue())),
  }),
  columnHelper.display({
    meta: { align: "right" },
    cell: (info) => {
      // biome-ignore lint/suspicious/noExplicitAny: Easier way for local scoping
      const meta = info.table.options.meta as any;

      return (
        <div className={styles.addNewButtonList}>
          <ButtonIcon
            icon={TbTag}
            onClick={(e) => {
              e.stopPropagation();
              meta?.onChangeStatus(info.row.original);
            }}
          />
          <ButtonIcon
            icon={TbTrash}
            onClick={(e) => {
              e.stopPropagation();
              meta?.onDelete(info.row.original);
            }}
          />
        </div>
      );
    },
    id: "actions",
  }),
];

interface MortgageRow {
  mortgage: Mortgage;
  borrower: Borrower;
  address: Address;
}

const useMortgageListTableData = () => {
  const mortgageIDs = useMortgageList();
  const mortgages = useMortgagesSuspense(mortgageIDs);

  const borrowerIDs = useMemo(
    () => uniq(mortgages.map((x) => x.clientID)),
    [mortgages],
  );
  const borrowers = useBorrowersSuspense(borrowerIDs);

  const addressIDs = useMemo(
    () => uniq(mortgages.map((x) => x.addressID)),
    [mortgages],
  );
  const addresses = useAddressesSuspense(addressIDs);

  return useMemo<MortgageRow[]>(() => {
    return mortgages.map((mortgage) => {
      const address = addresses.find((x) => x.ID === mortgage.addressID);

      if (!address) {
        throw new Error(`Unknown address for mortgage ${mortgage.ID}`);
      }

      const borrower = borrowers.find((x) => x.ID === mortgage.clientID);

      if (!borrower) {
        throw new Error(`Unknown borrower for mortgage ${mortgage.ID}`);
      }

      return {
        mortgage,
        borrower,
        address,
      };
    });
  }, [mortgages, addresses, borrowers]);
};

export const PageListMortgages = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [activeStatuses, setActiveStatuses] = useState<Set<RfqStatus>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState("");
  const allTableData = useMortgageListTableData();

  const tableData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allTableData.filter((row) => {
      if (activeStatuses.size > 0 && !activeStatuses.has(row.mortgage.status)) {
        return false;
      }
      if (query) {
        const borrowerName =
          `${row.borrower.firstName} ${row.borrower.lastName}`.toLowerCase();
        const address = formatAddress([
          row.address.line1,
          row.address.line2,
          row.address.city,
          row.address.state,
          row.address.zip_code,
        ]).toLowerCase();
        if (!borrowerName.includes(query) && !address.includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [allTableData, activeStatuses, searchQuery]);

  const onSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const toggleStatus = (status: RfqStatus) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };
  const navigate = useNavigate();
  const dialog = useDialog();
  const deleteMortgage = useMortgageDelete();

  const onChangeStatus = (row: MortgageRow) => {
    dialog.open(ChangeRfqStatusDialog, { mortgageID: row.mortgage.ID });
  };

  const onDelete = (row: MortgageRow) => {
    dialog.open(Dialog.Confirm, {
      onConfirm: () => deleteMortgage(row.mortgage.ID),
      variant: "danger",
      confirmLabel: "Delete",
      confirmIcon: TbTrash,
      title: "Are you sure you want to delete this RFQ?",
      children: (
        <>
          Borrower:{" "}
          <strong>
            {row.borrower.firstName} {row.borrower.lastName}
          </strong>
          <br />
          <br />
          Mortgage address:{" "}
          <strong>
            {formatAddress([
              row.address.line1,
              row.address.line2,
              row.address.city,
              row.address.state,
              row.address.zip_code,
            ])}
          </strong>
          <br />
          <br />
          Balance: <strong>{formatMoney(row.mortgage.data.balance)}</strong>
          <br />
          <br />
          <i>
            <strong>This action is irreversible.</strong>
          </i>
        </>
      ),
    });
  };

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    meta: {
      onChangeStatus,
      onDelete,
    },
  });

  return (
    <AppPage title="RFQs">
      <Container>
        <Grid className="mb-3">
          <Grid.Col xs={3}>
            <Card>
              <Card.Header>Filters</Card.Header>
              <Card.Body>
                <Input
                  placeholder="Search by name or address…"
                  value={searchQuery}
                  onChange={onSearch}
                />
              </Card.Body>
              <Card.Body>
                <p className={styles.filterSectionTitle}>Status</p>
                {RFQ_STATUSES.map((status) => (
                  <label key={status} className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={activeStatuses.has(status)}
                      onChange={() => toggleStatus(status)}
                    />
                    {RFQ_STATUS_LABELS[status]}
                  </label>
                ))}
              </Card.Body>
            </Card>
          </Grid.Col>
          <Grid.Col xs={9}>
            <Card>
              <Card.Header>
                Request for quotation (RFQ)
                <div className={styles.addNewButtonList}>
                  <Button
                    size="sm"
                    icon={TbPlus}
                    variant="ghost"
                    onClick={() => navigate(LinkGen.RFQ.New())}
                  >
                    Add manually
                  </Button>
                  <Button
                    size="sm"
                    icon={TbSparkles}
                    onClick={() => navigate(LinkGen.RFQ.Extract())}
                  >
                    Extract statement
                  </Button>
                </div>
              </Card.Header>
              <Table hover>
                <Table.Head>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Table.Row key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Table.HeaderCell
                          alignRight={
                            // biome-ignore lint/suspicious/noExplicitAny: Easy fix
                            (header.column.columnDef.meta as any)?.align ===
                            "right"
                          }
                          sort={
                            header.column.getCanSort()
                              ? header.column.getIsSorted()
                              : undefined
                          }
                          key={header.id}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Table.HeaderCell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Head>
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row
                      key={row.id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(LinkGen.RFQ.Detail(row.original.mortgage.ID))
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell
                          key={cell.id}
                          alignRight={
                            // biome-ignore lint/suspicious/noExplicitAny: Easy fix
                            (cell.column.columnDef.meta as any)?.align ===
                            "right"
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                  {/*{mortgageIDs.map((x) => (*/}
                  {/*	<MortgageRow key={x} ID={x} />*/}
                  {/*))}*/}
                  {tableData.length === 0 && (
                    <Table.Row>
                      <Table.Cell colSpan={8}>
                        <NoContent
                          label="No RFQs found"
                          sublabel="You can add a new one from the top right"
                          image="/select-option.svg"
                        />
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              {table.getPageCount() > 1 && (
                <Pagination
                  pageIndex={table.getState().pagination.pageIndex}
                  pageCount={table.getPageCount()}
                  canPreviousPage={table.getCanPreviousPage()}
                  canNextPage={table.getCanNextPage()}
                  onPreviousPage={table.previousPage}
                  onNextPage={table.nextPage}
                />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Container>
    </AppPage>
  );
};
